"use client";


import { type ComponentProps, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

type ImageDimensions = {
  width: number;
  height: number;
};

export default function ProductPageZoomLink({
  className,
  children,
  href,
  type = "image",
  ...props
}: Omit<ComponentProps<"a">, "type"> & { type?: "image" | "video" }) {
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    width: 2000,
    height: 2000,
  });

  useEffect(() => {
    if (!href) {
      return;
    }

    /**
     * Since we don't get the media dimensions from Norce and Loop54, we need to grab the width and height from the media itself.
     * Otherwise the zoom won't work properly with PhotoSwipe.
     */
    if (type === "video") {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = href;

      video.onloadedmetadata = () => {
        setImageDimensions({
          width: video.videoWidth || 2000,
          height: video.videoHeight || 2000,
        });
      };

      return;
    }

    const image = new Image();
    image.src = href;

    image.onload = () => {
      setImageDimensions({
        width: image.width,
        height: image.height,
      });
    };
  }, [href, type]);

  return (
    <a
      className={cn("hover:cursor-zoom-in", className)}
      data-pswp-src={href}
      data-pswp-type={type === "video" ? "video" : undefined}
      data-pswp-width={imageDimensions.width}
      data-pswp-height={imageDimensions.height}
      target="_blank"
      {...props}
    >
      {children}
    </a>
  );
}
